import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function TenantSmsDialog({ tenant, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !tenant) return;
    setForm({ phone: tenant.phone || "", sms_carrier: tenant.sms_carrier || "", sms_opt_in: !!tenant.sms_opt_in });
    if (!carriers.length) api.get("/settings/notifications").then((r) => setCarriers(r.data.carriers)).catch(() => {});
  }, [open, tenant]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try { await api.put(`/tenants/${tenant.id}/sms`, form); toast.success("Tenant contact saved"); onSaved?.(); onOpenChange(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tenant-sms-dialog">
        <DialogHeader><DialogTitle>Phone & text reminders · {tenant?.name}</DialogTitle>
          <DialogDescription>Rent-day nudges go to the tenant's email, and by text when a mobile number and carrier are on file.</DialogDescription></DialogHeader>
        {form && (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Mobile number</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="tenant-sms-phone" /></div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
              <div><div className="text-sm font-semibold text-slate-800">Send rent reminders by text</div><div className="text-xs text-slate-500">Uses the carrier's free email-to-SMS gateway</div></div>
              <Switch checked={form.sms_opt_in} onCheckedChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))} data-testid="tenant-sms-opt-in" />
            </div>
            <div className={`space-y-1.5 ${form.sms_opt_in ? "" : "opacity-50"}`}><Label className="text-xs">Carrier</Label>
              <Select value={form.sms_carrier} onValueChange={(v) => setForm((f) => ({ ...f, sms_carrier: v }))} disabled={!form.sms_opt_in}>
                <SelectTrigger data-testid="tenant-sms-carrier"><SelectValue placeholder="Pick a carrier" /></SelectTrigger>
                <SelectContent>{carriers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={saving || !form} className="w-full gap-2" data-testid="tenant-sms-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
